import type { NextPage } from 'next';
import { Box, ResponsiveSection } from '@stitch-fix/mode-react';
import { z } from 'zod';
import { Layout } from '../../components/Layout';
import { ImageHalfScreenHero } from '../../components/ImageHalfScreenHero';
import { aboutSchema } from './schema';
import { SEOData } from '../../components/SEOData';
import { AboutPersons } from '../../components/AboutPersons';
import { Divider } from '../../components/Divider';
import { GetStartedBox } from '../../components/GetStartedBox';
import { JsonRteRenderer } from '../../components/JsonRteRenderer';
import { getCachedEntry } from '../../utils/requests/contentstack/cachedRequests';
import { serverLogger } from '../../utils/loggers/server';

type AboutProps = z.infer<typeof aboutSchema>;

export const getStaticProps = async () => {
  try {
    const entry = await getCachedEntry({
      contentType: 'about',
      schema: aboutSchema,
      logger: serverLogger,
    });

    return {
      props: entry.data,
      revalidate: 300,
    };
  } catch (error) {
    serverLogger({
      level: 'error',
      team: 'client-onboarding',
      message: 'Failed to fetch required page data - /about',
      context: {
        error,
        page: '/about',
        severity: 'critical',
      },
    });

    throw new Error('Required data not provided to render /about');
  }
};

const AboutPage: NextPage<AboutProps> = ({ data }) => {
  return (
    <Layout viewEventTrackingName="corporate">
      <ResponsiveSection
        as="main"
        mt="0"
        width={{ sm: 'full-bleed' }}
        gutter="none"
        // setting this id allows the "Skip to page content" link to render from knit
        id="knit-main-content"
      >
        <SEOData {...data.seoData} />
        <ImageHalfScreenHero {...data.imageHalfScreenHero} />
        <Box
          maxWidth="644px"
          px="1rem"
          mx="auto"
          my={{ sm: '3rem', lg: '4rem' }}
        >
          <JsonRteRenderer data={data.content} />
        </Box>
        <AboutPersons {...data.executiveTeam} />
        <Box maxWidth="960px" mx="auto">
          <Divider variant="section" />
        </Box>
        <AboutPersons {...data.board} />
        <GetStartedBox {...data.getStarted} />
      </ResponsiveSection>
    </Layout>
  );
};

export default AboutPage;
